import { Divider, Grid, Stack, Toolbar, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { indigo } from '@mui/material/colors';
import React, { ReactElement, ReactNode } from 'react';

type SectionProps = {
  title?: string;
  subtitle?: string;
  meta?: () => ReactNode;
  children: React.ReactNode;
  rightTopControl?: () => ReactNode;
  badge?: ReactElement;
};

export function Section({
  children,
  title,
  subtitle,
  rightTopControl,
  badge,
}: SectionProps) {
  return (
    <Stack>
      <Grid container>
        <Grid item xs>
          {title && (
            <>
              <Stack direction="row" gap={1}>
                <Box
                  component="h2"
                  sx={(theme) => ({
                    fontWeight: '400',
                    fontSize: '1em',
                    lineHeight: '24px',
                    letterSpacing: '0.15px',
                    color:
                      theme.palette.mode === 'dark' ? indigo[200] : '#303F9F',
                    marginTop: 0,
                    marginBottom: subtitle ? 0 : '1em',
                    whiteSpace: 'nowrap',
                  })}
                >
                  {title}
                </Box>
                {badge}
              </Stack>
              {subtitle && (
                <Box
                  component="h3"
                  sx={(theme) => ({
                    fontSize: '14px',
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.text.secondary
                        : '#000000',
                    opacity: theme.palette.mode === 'dark' ? 1 : '38%',
                    fontWeight: '400',
                    margin: 0,
                    marginBottom: '1em',
                  })}
                >
                  {subtitle}
                </Box>
              )}
            </>
          )}
        </Grid>
        <Grid item xs>
          <Grid container direction="row-reverse">
            <Grid item>{rightTopControl && rightTopControl()}</Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box sx={{ width: '100%' }}>{children}</Box>
    </Stack>
  );
}

export function SubSection({ title, children }: SectionProps) {
  const renderTitle = () =>
    title && (
      <Typography
        variant="h3"
        style={{
          width: '100%',
          fontSize: '0.875em',
          fontWeight: '700',
          marginTop: '2em',
        }}
      >
        {title}
      </Typography>
    );
  return (
    <Stack>
      {renderTitle()}
      <Box>{children}</Box>
    </Stack>
  );
}

export function SectionWithMeta({
  title,
  subtitle,
  meta,
  children,
}: SectionProps) {
  const renderTitle = () =>
    title && (
      <Typography
        component="h2"
        variant="h2"
        sx={(theme) => ({
          fontWeight: '400',
          fontSize: '1em',
          lineHeight: '24px',
          letterSpacing: '0.15px',
          color: theme.palette.mode === 'dark' ? indigo[200] : '#303F9F',
          marginTop: 0,
          marginBottom: subtitle ? 0 : '1em',
        })}
      >
        {title}
      </Typography>
    );
  const renderSubtitle = () =>
    subtitle && (
      <Typography
        component="h3"
        variant="h3"
        sx={(theme) => ({
          fontSize: '14px',
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.text.secondary
              : '#000000',
          opacity: theme.palette.mode === 'dark' ? 1 : '38%',
          fontWeight: '400',
          margin: 0,
          marginBottom: '1em',
        })}
      >
        {subtitle}
      </Typography>
    );

  const renderMeta = () => meta && meta();

  return (
    <Stack>
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          '&.MuiToolbar-root': { paddingLeft: 0, paddingRight: 0 },
        }}
      >
        <Stack>
          {renderTitle()}
          {renderSubtitle()}
        </Stack>
        {renderMeta()}
      </Toolbar>
      <Box sx={{ width: '100%' }}>{children}</Box>
    </Stack>
  );
}

export const SectionDivider = () => <Divider sx={{ marginY: '2em' }} />;
